import React, { useEffect, useState } from "react";
import {
  Card,
  Table,
  Input,
  message,
  ConfigProvider,
  Space,
  Image,
  Tag,
  Avatar,
} from "antd";
import { SearchOutlined } from "@ant-design/icons";
import "./moments.css";
import { routes } from "../../api/routes";
import { callApi } from "../../api/apiCaller";
import Loader from "../../components/loader/loader";
import NotificationBell from "../../components/notificationBell/notificationBell";
import dayjs from "dayjs";

const Moments = () => {
  const [moments, setMoments] = useState([]);
  const [allMoments, setAllMoments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    getMoments();
  }, []);

  const getMoments = () => {
    const getRes = (res) => {
      if (res.status === 200) {
        // Try multiple possible response structures
        let momentsData = 
          res.data?.data?.data || 
          res.data?.data || 
          res.data?.moments || 
          res.data || 
          [];
        
        // If it's not an array, try to extract array from it
        if (!Array.isArray(momentsData) && momentsData && typeof momentsData === 'object') {
          momentsData = Object.values(momentsData).find(item => Array.isArray(item)) || [];
        }
        
        if (!Array.isArray(momentsData)) {
          console.error("Moments data is not an array:", momentsData);
          message.error("Invalid response format from API");
          return;
        }
        
        if (momentsData.length === 0) {
          message.warning("No moments found");
        }
        
        setAllMoments(momentsData);
        setMoments(momentsData);
      } else {
        message.warning(`Received status ${res.status} from API`);
      }
    };

    callApi(
      "GET",
      routes.getAllMoments,
      null,
      setIsLoading,
      getRes,
      (error) => {
        const errorMsg = error?.response?.data?.message || error?.message || "Failed to load moments";
        message.error(errorMsg);
      }
    );
  };

  // No create/update/delete actions - read-only view

  const handleSearch = (value) => {
    const search = value.trim().toLowerCase();
    if (!search) {
      setMoments(allMoments);
      return;
    }

    const filtered = allMoments.filter((moment) => {
      const title = moment.title || "";
      const categoryTitle = moment.category?.title || "";
      const subCategoryTitle = moment.subCategory?.title || "";
      const creatorName = moment.creator?.name || "";
      const connectionName = moment.connection?.name || "";

      const haystack = [
        title,
        categoryTitle,
        subCategoryTitle,
        creatorName,
        connectionName,
      ]
        .join(" ")
        .toLowerCase();

      return haystack.includes(search);
    });

    setMoments(filtered);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "accepted":
        return "green";
      case "pending":
        return "orange";
      case "cancelled":
        return "red";
      case "connection-suggested-time":
        return "blue";
      case "connection-rejected":
        return "red";
      default:
        return "default";
    }
  };

  const columns = [
    {
      title: "No",
      key: "index",
      render: (text, record, index) => index + 1,
      width: 60,
    },
    {
      title: "Category Image",
      dataIndex: "category",
      key: "categoryImage",
      width: 100,
      render: (category) => (
        <Image
          src={category?.image}
          alt={category?.title || "Category"}
          width={60}
          height={60}
          style={{ objectFit: "cover", borderRadius: 4 }}
          fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29BwNZwQxT0g2F4U0B0J9BsKxO4L4J4BdGTGMjQCYQe4B"
        />
      ),
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      render: (text) => <span style={{ fontWeight: 500, color: "#333" }}>{text || "N/A"}</span>,
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      render: (category) => category?.title || "N/A",
      width: 120,
    },
    {
      title: "Sub Category",
      dataIndex: "subCategory",
      key: "subCategory",
      render: (subCategory) => subCategory?.title || "N/A",
      width: 130,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 150,
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {status ? status.replace(/-/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()) : "N/A"}
        </Tag>
      ),
    },
    {
      title: "Creator",
      dataIndex: "creator",
      key: "creator",
      width: 150,
      render: (creator) => (
        <Space>
          <Avatar src={creator?.image} size="small" />
          <span>{creator?.name || "N/A"}</span>
        </Space>
      ),
    },
    {
      title: "Connection",
      dataIndex: "connection",
      key: "connection",
      width: 150,
      render: (connection) => (
        <Space>
          <Avatar src={connection?.image} size="small" />
          <span>{connection?.name || "N/A"}</span>
        </Space>
      ),
    },
    {
      title: "Date & Time",
      dataIndex: "momentDateAndTime",
      key: "momentDateAndTime",
      width: 180,
      render: (date) => (date ? dayjs(date).format("DD MMM YYYY, HH:mm") : "N/A"),
    },
    {
      title: "Repeat",
      key: "repeat",
      width: 100,
      render: (_, record) => {
        if (record.isRepeatWeekly) return <Tag color="blue">Weekly</Tag>;
        if (record.isRepeatMonthly) return <Tag color="purple">Monthly</Tag>;
        return <Tag>None</Tag>;
      },
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => (date ? dayjs(date).format("DD MMM YYYY") : "N/A"),
      width: 120,
    },
  ];

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: "#FF66B2",
        },
      }}
    >
      <div className="moments-container">
        <Loader loading={isLoading} />
        <div className="moments-header">
          <h1 className="moments-title">Moment Management</h1>
          <NotificationBell />
        </div>

        <div className="moments-content">
          <Card className="moments-card">
            <div className="moments-actions">
              <Input
                placeholder="Search by title, category, subcategory, creator or connection..."
                prefix={<SearchOutlined />}
                allowClear
                onChange={(e) => handleSearch(e.target.value)}
                style={{ maxWidth: 400 }}
              />
            </div>

            <Table
              columns={columns}
              dataSource={moments}
              rowKey="_id"
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} moments`,
                pageSizeOptions: ["10", "20", "50", "100"],
              }}
              className="moments-table"
            />
          </Card>
        </div>
      </div>
    </ConfigProvider>
  );
};

export default Moments;
